import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ISbStoryData } from '@storyblok/js';
import { Observable, firstValueFrom, map } from 'rxjs';

export type Schema = string | number | Schema[] | { [key: string]: Schema };

interface Rating {
  name: string;
  min: number;
  max: number;
  average: number;
  total: number;
}

interface RawSchemaItemText {
  key: string;
  component: 'SchemaItemText';
  value: string;
}

interface RawSchemaItemNumber {
  key: string;
  component: 'SchemaItemNumber';
  value: number;
}

interface RawSchemaItemArray {
  key: string;
  component: 'SchemaItemArray';
  items: RawSchemaItem[];
}

interface RawSchemaItemObject {
  key: string;
  component: 'SchemaItemObject';
  items: RawSchemaItem[];
  ifKeyHasValue?: string;
}

interface RawSchemaItemDate {
  key: string;
  component: 'SchemaItemDate';
  value: string;
}

type RawSchemaItem =
  | RawSchemaItemText
  | RawSchemaItemNumber
  | RawSchemaItemArray
  | RawSchemaItemObject
  | RawSchemaItemDate;

@Injectable({
  providedIn: 'root',
})
export class StoryblokSchemaService {
  private http = inject(HttpClient);

  private replaceStoryValues(
    story: unknown,
    value: string,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): string | number {
    const regex = /{{(.*?)}}/g;
    let match: string[];
    while ((match = regex.exec(value))) {
      const path = match[1].split('.');
      const replaceVal = this.getReplaceVal(story, path, relationships);
      if (replaceVal && ['string', 'number'].includes(typeof replaceVal)) {
        value = value.replace(match[0], replaceVal as string);
      }
    }

    // RATING[RATING[schemaTestPageRating,best]]
    const regex2 = /RATING\[(.*?)\]/g;
    let match2: string[];
    while ((match2 = regex2.exec(value))) {
      const [ratingName, ratingType] = match2[1].split(',');

      const rating = ratings?.find(r => r.name === ratingName);
      if (!rating) continue;

      const ratingValue = rating[ratingType];
      if (!ratingValue) {
        console.log(match2, rating, ratingType);
      }
      value = value.replace(match2[0], ratingValue.toString());
    }

    return value;
  }

  private getSchemaValue = (
    item: RawSchemaItem,
    story: unknown,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema => {
    if (item.component === 'SchemaItemText') {
      // item.value might be "prefix: {{story.content.title}} suffix" for example
      // so we need to replace the {{story.content.title}} part with the actual value
      return this.replaceStoryValues(story, item.value, relationships, ratings);
    } else if (item.component === 'SchemaItemNumber') {
      return Number(item.value);
    } else if (item.component === 'SchemaItemObject') {
      if (item.ifKeyHasValue) {
        const value = this.replaceStoryValues(story, item.ifKeyHasValue, relationships, ratings) as string;
        if (!value || value.includes('{') || value.includes('[')) return;
      }
      return this.getSchemaObject(item.items, {}, story, relationships, ratings);
    } else if (item.component === 'SchemaItemArray') {
      return this.getSchemaArray(item.items, [], story, relationships, ratings);
    } else if (item.component === 'SchemaItemDate') {
      // 2023-12-21 23:31
      const year = Number(item.value.slice(0, 4));
      const month = Number(item.value.slice(5, 7)) - 1;
      const day = Number(item.value.slice(8, 10));
      const hour = Number(item.value.slice(11, 13));
      const minute = Number(item.value.slice(14, 16));
      return new Date(year, month, day, hour, minute).toISOString();
    }
  };

  private getSchemaArray = (
    schemaItems: RawSchemaItem[],
    array: Schema[],
    story: unknown,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema[] => {
    schemaItems.forEach(item => {
      const value = this.getSchemaValue(item, story, relationships, ratings);
      if (value === undefined) return;
      array.push(value);
    });
    return array;
  };

  private getSchemaObject = (
    schemaItems: RawSchemaItem[],
    object: Schema,
    story: unknown,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema => {
    schemaItems.forEach(item => {
      const value = this.getSchemaValue(item, story, relationships, ratings);
      if (value === undefined) return;
      object[item.key] = value;
    });
    return object;
  };

  public getSchema = async (story: { content: { schema: string } }, relationships: ISbStoryData[]): Promise<Schema> => {
    const schema = relationships.find(re => re.uuid === story.content.schema)?.content as {
      requiresCustomRatings: boolean;
      requiresExternalRating: boolean;
      items: RawSchemaItem[];
    };
    if (!schema) return;

    const ratings = schema.requiresCustomRatings
      ? await firstValueFrom(this.getCustomRatings())
      : schema.requiresExternalRating
      ? [await firstValueFrom(this.getExtenalRating())]
      : undefined;

    return this.getSchemaObject(
      schema.items,
      {
        '@context': 'https://schema.org/',
      },
      story,
      relationships,
      ratings
    );
  };

  public getPreviewSchema = (story: { content: { items: RawSchemaItem[] } }): Schema => {
    if (!story.content.items) return {};
    return this.getSchemaObject(
      story.content.items,
      {
        '@context': 'https://schema.org/',
      },
      story,
      []
    );
  };

  private getReplaceVal(story: unknown, path: string[], relationships: ISbStoryData[]) {
    let current = story;

    for (const key of path) {
      current = current[key];
      const isUuid =
        typeof current === 'string' &&
        current.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
      if (isUuid) {
        const relationship = relationships.find(re => re.uuid === current);
        if (relationship) current = relationship.content;
      }
      if (!current) break;
    }
    return current;
  }

  private getCustomRatings(): Observable<Rating[]> {
    return this.http
      .get<{ id: 1; ratingName: 'testRating1'; '1_star': 0; '2_star': 0; '3_star': 0; '4_star': 0; '5_star': 0 }[]>(
        '/internal-api/custom-ratings'
      )
      .pipe(
        map(resp => {
          return resp.map(r => {
            const total = r['1_star'] + r['2_star'] + r['3_star'] + r['4_star'] + r['5_star'];

            let min = 5;

            if (r['1_star'] > 0) min = 1;
            else if (r['2_star'] > 0) min = 2;
            else if (r['3_star'] > 0) min = 3;
            else if (r['4_star'] > 0) min = 4;

            let max = 1;

            if (r['5_star'] > 0) max = 5;
            else if (r['4_star'] > 0) max = 4;
            else if (r['3_star'] > 0) max = 3;
            else if (r['2_star'] > 0) max = 2;

            let average = 0;

            average += r['1_star'] * 1;
            average += r['2_star'] * 2;
            average += r['3_star'] * 3;
            average += r['4_star'] * 4;
            average += r['5_star'] * 5;

            if (total > 0) average /= total;

            return {
              name: r.ratingName,
              min,
              max,
              average,
              total,
            };
          });
        })
      );
  }

  private getExtenalRating(): Observable<Rating> {
    return this.http.get<Rating>('/internal-api/external-rating');
  }
}
